import React, { SyntheticEvent, FormEvent } from "react";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles from "@material-ui/core/styles/withStyles";
import { Typography, Card } from "@material-ui/core";
import { RouteComponentProps } from "react-router";

import {
  PageHeader,
  PageRoot,
  TextField,
  Button,
  Checkbox
} from "../../components";
import routes from "../../constants/routes";
import { connect } from "react-redux";
import { registerActions } from "../../reducers/register";
import { RestrictionNoticeSearchResult } from "../../constants/interfaces";
import api from "../../utils/api";
import RestrictionNotice from "./RestrictionNotice";
import { Message } from "../../components/Messages";
import { OnBeforeUnload } from "../../components/navigation/OnBeforeUnload";

const styles = (theme: Theme) =>
  createStyles({
    button: {
      margin: theme.spacing(1)
    },
    checkboxWrapper: {
      marginTop: theme.spacing(1),
      width: "100%"
    },
    inputWrapper: {
      alignItems: "center",
      flexFlow: "row wrap",
      display: "flex"
    },
    noticeCell: {
      margin: theme.spacing(1),
      marginTop: 0
    },
    noticeContainer: {
      marginTop: theme.spacing(4),
      padding: theme.spacing(4)
    },
    noticeIcon: {
      marginRight: theme.spacing(1)
    },
    noticeIconRow: {
      alignItems: "center",
      display: "flex",
      flexFlow: "row wrap"
    },
    noticeRow: {
      display: "flex",
      flexFlow: "row wrap"
    },
    noticeSectionTitle: {
      margin: theme.spacing(1),
      width: "100%"
    },
    productList: {
      margin: 0,
      paddingLeft: theme.spacing(2)
    },
    root: {
      alignItems: "center",
      display: "flex",
      flexFlow: "column"
    },
    textField: {
      margin: theme.spacing(1)
    },
    error: {
      marginTop: theme.spacing(4)
    }
  });

type State = {
  declarationChecked: boolean;
  nhi: string;
  surname: string;
  error: null | Error;
  searchResult: RestrictionNoticeSearchResult | null;
};

type Props = RouteComponentProps & {
  classes: any;
  getRestrictionNotice: (
    nhi: string,
    surname: string
  ) => Promise<RestrictionNoticeSearchResult>;
};

class SearchRestrictionNotice extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      declarationChecked: false,
      nhi: "",
      surname: "",
      error: null,
      searchResult: null
    };
    this.onClear = this.onClear.bind(this);
    this.onSearch = this.onSearch.bind(this);
    this.onUpdateField = this.onUpdateField.bind(this);
    this.toggleDeclaration = this.toggleDeclaration.bind(this);
  }

  async onSearch(e: FormEvent) {
    e.preventDefault();
    const { nhi, surname } = this.state;
    try {
      const { data }: { data: RestrictionNoticeSearchResult } = await api.get(
        `/registers/searchRestrictionNotices?surname=${surname.trim()}&nhi=${nhi.trim()}`
      );

      if (!data.cmAcmCaseIdentifier) {
        return this.setState({
          error: new Error(
            `Oops! No results matching  "${surname}" and "${nhi}"`
          ),
          searchResult: null
        });
      }

      if (data.restrictionNotices.length === 0) {
        return this.setState({
          error: new Error(`Oops!. No Active Notices to Display`),
          searchResult: null
        });
      }

      this.setState({
        error: null,
        searchResult: data,
        declarationChecked: false,
        nhi: "",
        surname: ""
      });
    } catch (e) {
      this.setState({
        error: new Error(
          `Oops! No results matching  "${surname}" and "${nhi}"`
        ),
        searchResult: null
      });
    }
  }

  onClear() {
    this.setState({
      nhi: "",
      declarationChecked: false,
      error: null,
      searchResult: null,
      surname: ""
    });
  }

  onUpdateField(field: "nhi" | "surname", value: string) {
    const state = { ...this.state, noResults: false };
    state[field] = value;
    this.setState(state);
  }

  toggleDeclaration(e: SyntheticEvent, checked: boolean) {
    this.setState({
      declarationChecked: checked
    });
  }

  render() {
    const { classes, history } = this.props;
    const { declarationChecked, nhi, searchResult, surname } = this.state;
    const canSubmitSearch: boolean = declarationChecked && !!nhi && !!surname;
    return (
      <PageRoot>
        <PageHeader
          label="Registers"
          breadcrumbLinks={[
            { label: routes.HOME.label, route: routes.HOME.url }
          ]}
        />
        {(nhi || surname) && <OnBeforeUnload />}
        <div className={classes.root}>
          <div>
            <Typography component="h2" variant="h5">
              Search Restriction Notice Register
            </Typography>
            <form onSubmit={this.onSearch}>
              <Checkbox
                checked={declarationChecked}
                color="secondary"
                className={classes.checkboxWrapper}
                label="I confirm I am authorised to conduct this search"
                onChange={this.toggleDeclaration}
              />
              <div className={classes.inputWrapper}>
                <TextField
                  className={classes.textField}
                  id="surname"
                  label="Surname"
                  onChange={e => this.onUpdateField("surname", e.target.value)}
                  placeholder="Enter surname"
                  value={surname}
                />
                <TextField
                  className={classes.textField}
                  id="nhi"
                  label="NHI"
                  onChange={e => this.onUpdateField("nhi", e.target.value)}
                  placeholder="Enter NHI"
                  value={nhi}
                />
                <Button
                  className={classes.button}
                  disabled={!canSubmitSearch}
                  id="submit"
                  color="primary"
                  type="submit"
                >
                  Search
                </Button>
                {searchResult && searchResult.restrictionNotices.length && (
                  <Button
                    className={classes.button}
                    id="clear"
                    onClick={this.onClear}
                  >
                    Clear
                  </Button>
                )}
                <Button
                  onClick={() => {
                    history.push(`${routes.HOME.url}`);
                  }}
                  id="close"
                >
                  Close
                </Button>
              </div>
            </form>
          </div>

          {this.state.error ? (
            <Card className={classes.error}>
              <Message
                message={this.state.error.message}
                variant="info"
                onClose={() => {
                  this.setState({
                    error: null
                  });
                }}
              />
            </Card>
          ) : (
            <RestrictionNotice searchResult={searchResult} />
          )}
        </div>
      </PageRoot>
    );
  }
}

const mapDispatchToProps = {
  getRestrictionNotice: registerActions.getRestrictionNotice
};

export const styled = withStyles(styles)(SearchRestrictionNotice);
export default connect(
  null,
  mapDispatchToProps
)(styled);
